import React, { useEffect, useMemo, useRef, useState } from "react";
import { sentenceCaseLabel, useTranslation } from "../i18n";

import { createPortal } from "react-dom";
import LoadingSpinner from "../Components/LoadingSpinner";

import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  getOrderAction,
  respondToOrderAction,
  updateOrderStatusAction,
} from "../actions/order";
import { backendUrl } from "../Router/constant";

const readOrderText = (value) => {
  if (typeof value === "string" || typeof value === "number") return String(value);
  if (!value || typeof value !== "object") return "";
  return value.name || value.title || value.label || value.orderNumber || value.username || value.email || "";
};

const getPublicInquiryId = (...values) => {
  const candidates = values.flatMap((value) =>
    value && typeof value === "object"
      ? [
          value.inquiryId,
          value.displayInquiryId,
          value.referenceNumber,
          value.code,
          value.id,
        ]
      : [value],
  );
  const publicId = candidates
    .filter((value) => typeof value === "string" || typeof value === "number")
    .map((value) => String(value).trim())
    .find((value) => value && !/^[a-f0-9]{24}$/i.test(value));
  return publicId || "—";
};
const formatOrderDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
};

const formatOrderDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleString(undefined, {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
};

const formatTimelineDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? String(value)
    : new Intl.DateTimeFormat("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }).format(date);
};

const formatQuantity = (quantity, unit = "") => {
  const value = quantity && typeof quantity === "object"
    ? quantity.amount ?? quantity.value ?? quantity.quantity
    : quantity;
  const quantityUnit = (quantity && typeof quantity === "object" ? quantity.unit : "") || unit;
  if (value === undefined || value === null || value === "") return "—";
  const unitText = readOrderText(quantityUnit);
  return `${value}${unitText ? ` ${unitText}` : ""}`;
};

const getCurrencySymbol = (currency) => {
  const value = String(currency || "").trim();
  const symbols = { USD: "$", EUR: "€", GBP: "£", INR: "₹", AED: "د.إ", SAR: "﷼" };
  if (symbols[value.toUpperCase()]) return symbols[value.toUpperCase()];
  if (value.length === 1 || /[^A-Z]/i.test(value)) return value;
  try {
    return new Intl.NumberFormat("en", {
      style: "currency",
      currency: value.toUpperCase(),
    }).formatToParts(0).find((part) => part.type === "currency")?.value || value;
  } catch {
    return value;
  }
};

const formatPrice = (price, currency) => {
  if (price === undefined || price === null || price === "") return "—";
  const numericPrice = Number(price);
  const amount = Number.isFinite(numericPrice)
    ? numericPrice.toLocaleString(undefined, { maximumFractionDigits: 2 })
    : String(price);
  const symbol = getCurrencySymbol(currency);
  return `${symbol ? `${symbol} ` : ""}${amount}`;
};

const formatAddress = (address = {}) =>
  [address.address, address.city, address.state, address.country, address.pincode]
    .filter((part) => part !== undefined && part !== null && String(part).trim())
    .map((part) => String(part).trim())
    .join(", ") || "—";

const getOrderImageUrl = (image) => {
  if (typeof image === "string") {
    const value = image.trim();
    if (!value || /^[a-z]:[\\/]/i.test(value) || /^\\\\/.test(value)) return "";
    return /^(https?:|data:|blob:|\/)/i.test(value)
      ? value
      : `${backendUrl}/uploads/${encodeURIComponent(value)}`;
  }
  if (!image || typeof image !== "object") return "";

  const imageId = image.uploadId || image.fileId || image.imageId || image._id || image.id;
  if (imageId) return `${backendUrl}/uploads/${encodeURIComponent(imageId)}`;
  const imageUrl = image.url || image.path || "";
  if (/^(https?:|data:|blob:|\/)/i.test(imageUrl)) return imageUrl;
  return "";
};

const mapOrderToOperation = (order, routeId) => {
  if (!order || typeof order !== "object") return null;

  const requirements = Array.isArray(order.requirements) ? order.requirements : [];
  const productEntries = requirements.flatMap((requirement) => {
    const products = Array.isArray(requirement.products)
      ? requirement.products
      : [requirement.product || requirement.productId].filter(Boolean);
    return products.map((entry) => ({ requirement, entry }));
  });
  const legacyLines = order.items || order.products || order.orderItems || order.lines || [];
  if (!productEntries.length && Array.isArray(legacyLines)) {
    productEntries.push(...legacyLines.map((entry) => ({ requirement: order, entry })));
  }
  const firstEntry = productEntries[0]?.entry || {};
  const firstRequirement = productEntries[0]?.requirement || requirements[0] || {};
  const rawProduct = firstEntry.product || firstEntry.productId || firstEntry.item ||
    order.product || order.productId || firstEntry;
  const product = rawProduct && typeof rawProduct === "object" ? rawProduct : {};
  const client = order.client || order.buyer || order.customer || order.user || {};
  const assignedVendor = order.assignedVendor || order.vendor || order.vendorId || {};
  const clientAddress = order.address || client.address || {};
  const vendorOffer = order.vendorOffer || {};
  const vendorResponse = order.vendorResponse || {};
  const currentStatusLabel = readOrderText(order.currentStatusLabel);
  const rawStatus = readOrderText(order.currentStatus || order.status || order.orderStatus) ||
    readOrderText(vendorResponse.status) || "Pending";
  const statusSlug = rawStatus.toLowerCase().trim().replace(/[\s-]+/g, "_");
  const shipment = order.shipment || order.shipping || order.shippingDetails || order.tracking || {};
  const shippingStatusValue =
    order.currentStatus || shipment.status || shipment.currentStatus ||
    order.shippingStatus?.status || order.shippingStatus?.currentStatus ||
    order.shippingStatus?.value || order.shippingStatus ||
    order.shipmentStatus?.status || order.shipmentStatus || rawStatus;
  const rawShippingStatus = readOrderText(shippingStatusValue) || "Pending";
  const history = Array.isArray(order.orderHistory) ? order.orderHistory : [];
  const currentHistory = [...history].reverse().find((entry) =>
    String(entry.key || "").toLowerCase() === statusSlug,
  ) || (statusSlug === "in_transit"
    ? [...history].reverse().find((entry) => entry.customStatus)
    : null) || {};
  const customShipmentStatus = readOrderText(
    currentHistory.customStatus || order.customStatus || shipment.customStatus ||
      order.shippingStatus?.customStatus || order.shipmentStatus?.customStatus ||
      shipment.currentLocation || shipment.location || order.currentLocation || order.shippingLocation,
  );
  const stageText = readOrderText(
    order.currentStage || order.stage || shippingStatusValue || order.tracking?.status,
  ) || rawStatus;
  const stageSlug = stageText.toLowerCase();
  const currentStage = /complete|deliver/.test(stageSlug)
    ? "Completed"
    : /ship|transit/.test(stageSlug)
      ? "Shipment"
      : /finance|payment|quote/.test(stageSlug)
        ? "Finance review"
        : /document|progress|review/.test(stageSlug)
          ? "Documents"
          : "Operation created";
  const quantity = requirements
    .map((requirement) => formatQuantity(
      requirement.quantity,
      requirement.unit || requirement.quantityUnit,
    ))
    .filter((value) => value !== "—")
    .join(", ") || formatQuantity(
      firstEntry.quantity ?? product.quantity ?? order.quantity,
      firstEntry.unit || firstEntry.quantityUnit || product.unit || order.unit || order.quantityUnit,
    );
  const completionDate = formatOrderDate(
    order.expectedDeliveryDate || order.estimatedCompletionDate || order.completionDate || order.deliveryDate,
  );
  const deliveredHistory = history.find((entry) => entry.key === "delivered");
  const isComplete = /complete|deliver/.test(statusSlug) || deliveredHistory?.isCompleted === true;
  const image = product.mainImage || firstEntry.mainImage || product.image || firstEntry.image ||
    product.thumbnail || firstEntry.thumbnail || product.images?.[0] || firstEntry.images?.[0] ||
    order.productImage || order.image || "";
  const operationStatusClass = /complete|deliver/.test(statusSlug)
    ? "completed"
    : /cancel|reject/.test(statusSlug)
      ? "cancelled"
      : /accept|approv/.test(statusSlug)
        ? "accepted"
        : ["pending", "in_transit", "initiated", "order_created", "order_placed"].includes(statusSlug)
          ? "pending"
          : statusSlug;
  const productNames = [...new Set(productEntries.map(({ entry }) => {
    const raw = entry.product || entry.productId || entry.item || entry;
    return readOrderText(
      raw?.productName || raw?.name || raw?.title || raw?.label || raw,
    ) || readOrderText(entry.productName || entry.name);
  }).filter(Boolean))];
  const commodity = productNames.join(", ") || readOrderText(product) || readOrderText(firstEntry.productName || firstEntry.name) ||
    readOrderText(
      product.productName || product.name || product.title ||
      order.productName || order.itemName || order.commodity || order.title,
    ) || "—";
  const productDescription = readOrderText(
    product.description || product.productDescription || product.details ||
      firstEntry.productDescription || firstEntry.description ||
      firstRequirement.productDescription || firstRequirement.description ||
      order.productDescription,
  ) || "—";
  const sectorNames = [...new Set(requirements.map((requirement) =>
    readOrderText(requirement.sector || requirement.sectorId) || requirement.sectorName || "",
  ).filter(Boolean))];
  const sector = sectorNames.join(", ") || "—";
  const targetPrice = formatPrice(firstRequirement.targetPrice, firstRequirement.currency);
  const clientCountry = readOrderText(client.country || client.countryName || clientAddress.country) || "—";
  const phone = readOrderText(client.phone);
  const countryCode = readOrderText(client.countryCode);
  const clientPhone = phone && countryCode && !phone.startsWith(countryCode)
    ? `${countryCode} ${phone}`
    : phone || "—";
  const historyItems = history.map((entry, index) => {
    const key = String(entry.key || "").trim().toLowerCase();
    const customStatus = readOrderText(entry.customStatus);
    const apiTitle = readOrderText(entry.title || entry.statusTitle || entry.name);
    const description = readOrderText(
      entry.description || entry.statusDescription || entry.notes || entry.note ||
        entry.message || entry.details,
    );
    const normalizedHistoryStatus = String(entry.status || "")
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, "_");
    const fallbackTitle = sentenceCaseLabel(key);
    const title = key === "in_transit"
      ? customStatus || apiTitle || fallbackTitle || "Order update"
      : apiTitle || fallbackTitle || "Order update";
    const isCompleted =
      entry.isCompleted === true || normalizedHistoryStatus === "completed";
    const isActive = !isCompleted && (
      entry.isActive === true ||
      entry.isCurrent === true ||
      ["active", "current", "in_progress", "in_transit"].includes(normalizedHistoryStatus)
    );

    return {
      id: String(entry._id || entry.id || `${entry.key || "history"}-${index}`),
      key,
      title,
      status: readOrderText(entry.status) || "Pending",
      isCompleted,
      isActive,
      completedAt: formatOrderDateTime(entry.completedAt),
      timelineDate: formatTimelineDate(entry.completedAt),
      hasCompletedAt: Boolean(entry.completedAt),
      customStatus,
      description,
      notes: readOrderText(entry.notes),
    };
  });
  const requirementSummary =
    readOrderText(order.detailedRequirements || order.inquiry?.detailedRequirements) ||
    [
      sector !== "—" ? `Sector: ${sector}` : "",
      commodity !== "—" ? `Product: ${commodity}` : "",
      quantity !== "—" ? `Quantity: ${quantity}` : "",
      targetPrice !== "—" ? `Target price: ${targetPrice}` : "",
    ].filter(Boolean).join(" · ") || "—";
  const vendorName = readOrderText(assignedVendor) || "—";
  const vendorEmail = readOrderText(assignedVendor.username || assignedVendor.email) || "—";
  const inquiry = order.inquiry || {};
  const inquiryId = getPublicInquiryId(
    inquiry,
    order.inquiryId,
    order.inquiry_id,
  );
  return {
    id: String(order.orderId || order.trackingId || order.orderNumber || order.orderNo || order.referenceNumber || order.code || order.id || order._id || routeId),
    databaseId: String(order._id || routeId || ""),
    trackingId: String(order.trackingId || "—"),
    inquiryId,
    createdDate: formatOrderDate(order.createdAt || order.createdOn || order.orderDate || order.date),
    createdAt: formatOrderDateTime(order.createdAt),
    updatedAt: formatOrderDateTime(order.updatedAt),
    operationStatus: sentenceCaseLabel(currentStatusLabel || rawShippingStatus),
    shippingStatus: rawShippingStatus.toLowerCase().trim().replace(/[\s-]+/g, "_"),
    customShipmentStatus: customShipmentStatus || "",
    shipmentNote: readOrderText(
      currentHistory.notes || order.note ||
        order.statusNote ||
        order.shippingNote ||
        order.shippingStatus?.note ||
        shipment.note ||
        shipment.notes,
    ),
    operationStatusClass,
    image: getOrderImageUrl(image),
    commodity,
    productDescription,
    quantity,
    grade: readOrderText(firstEntry.grade || product.grade || product.quality || product.specification || firstRequirement.grade || order.grade) || "—",
    sector,
    targetPrice,
    productQuantity: formatQuantity(product.quantity, product.unit),
    requirementSummary,
    seller: readOrderText(client) || readOrderText(order.clientName || order.buyerName || order.customerName) || "—",
    sellerEmail: readOrderText(client.email || client.username) || "—",
    sellerPhone: clientPhone,
    clientId: String(client._id || client.id || "—"),
    clientUserId: String(client.userId || "—"),
    country: clientCountry,
    address: formatAddress(clientAddress),
    flag: readOrderText(client.flag || client.countryFlag),
    vendorName,
    vendorId: String(assignedVendor.vendorId || assignedVendor._id || assignedVendor.id || "—"),
    vendorEmail,
    vendorApprovalStatus: readOrderText(assignedVendor.approvalStatus) || "—",
    vendorIsActive: typeof assignedVendor.isActive === "boolean" ? (assignedVendor.isActive ? "Yes" : "No") : "—",
    assignedVendorAt: formatOrderDateTime(order.assignedVendorAt),
    assignedVendorBy: readOrderText(order.assignedVendorBy) || "—",
    createdBy: readOrderText(order.createdBy) || "—",
    vendorOfferTitle: readOrderText(vendorOffer.title) || "—",
    vendorOfferDescription: readOrderText(vendorOffer.description) || "—",
    vendorOfferPrice: formatPrice(vendorOffer.price, vendorOffer.currency),
    vendorOfferCurrency: readOrderText(vendorOffer.currency) || "—",
    vendorResponseReason: readOrderText(vendorResponse.reason) || "—",
    vendorResponseAt: formatOrderDateTime(vendorResponse.respondedAt),
    vendorResponse: readOrderText(vendorResponse.status) || "Pending",
    orderHistory: historyItems,
    currentStage,
    nextStep: readOrderText(order.nextStep || order.nextAction) || "",
    completionDate,
    completionText: isComplete
      ? "Completed"
      : /cancel|reject/.test(statusSlug)
        ? "Cancelled"
        : statusSlug
          ? "In progress"
          : completionDate === "—"
            ? "—"
            : "Scheduled",
    completionClass: isComplete ? "green" : "normal",
    offerStatus: readOrderText(order.vendorResponse?.status || order.offerStatus || order.responseStatus) || "Pending",
  };
};

const emptyOperation = {
  id: "",
  createdDate: "—",
  operationStatus: "Pending",
  operationStatusClass: "pending",
  image: "",
  commodity: "—",
  productDescription: "—",
  quantity: "—",
  grade: "—",
  seller: "—",
  sellerEmail: "—",
  country: "—",
  flag: "",
  verified: false,
  currentStage: "Operation created",
  nextStep: "",
  completionDate: "—",
  completionText: "—",
  completionClass: "normal",
  offerStatus: "Pending",
};
const operationStages = [
  {
    title: "Operation created",
    description:
      "The operation has been created and all initial trade information has been recorded.",
  },

  {
    title: "Finance review",
    description:
      "Payment terms, financing requirements and financial documentation are being reviewed.",
  },

  {
    title: "Documents",
    description:
      "Trade, compliance and shipping documents are being prepared and verified.",
  },

  {
    title: "Shipment",
    description:
      "The goods are prepared for shipment or currently moving toward the delivery destination.",
  },

  {
    title: "Completed",
    description:
      "All required stages are complete and the operation has been successfully closed.",
  },
];

const shippingStatuses = [
  "Order created",
  "Order placed",
  "Other",
  "Delivered",
];

const getCurrentShippingStatus = (operation) => {
  const status = `${operation.shippingStatus || ""} ${operation.operationStatus || ""}`
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

  if (/deliver|complete/.test(status)) return "Delivered";
  if (/in_transit|transit|ship/.test(status) || operation.customShipmentStatus) return "Other";
  if (/order_accepted|accept/.test(status)) return "Order accepted";
  if (/order_created|created/.test(status)) return "Order created";
  if (/order_placed|place|pending/.test(status)) return "Order placed";
  return "";
};

const stageNextSteps = {
  "Operation created":
    "Financial assessment and commercial review will begin next.",

  "Finance review":
    "Complete finance review and prepare the required operation documents.",

  Documents: "Complete document verification and prepare the shipment.",

  Shipment: "Complete shipment, customs clearance and final delivery.",

  Completed: "Operation completed successfully.",
};

const getStageIndex = (currentStage) => {
  switch (currentStage) {
    case "Finance review":
      return 1;

    case "In progress":
      return 2;

    case "Shipment":
      return 3;

    case "Completed":
      return 4;

    default:
      return 0;
  }
};

const getProgress = (stageIndex) => {
  return Math.round(((stageIndex + 1) / operationStages.length) * 100);
};

export default function OperationDetails() {
  const { t, text, status: translateStatus } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const {
    detail: orderDetail,
    detailLoading,
    detailError,
    statusUpdateLoading,
    responseLoading,
  } = useSelector((state) => state.order || {});
  const dropdownRef = useRef(null);
  const shippingDropdownRef = useRef(null);

  useEffect(() => {
    if (id) dispatch(getOrderAction(id));
  }, [dispatch, id]);

  const mappedOperation = useMemo(() => mapOrderToOperation(orderDetail, id), [orderDetail, id]);
  const operation = mappedOperation || { ...emptyOperation, id: id || "" };
  const orderHistory = Array.isArray(operation.orderHistory) ? operation.orderHistory : [];
  const currentShippingStatus = getCurrentShippingStatus(operation);
  const isDelivered =
    currentShippingStatus === "Delivered" ||
    orderHistory.some((entry) => entry.key === "delivered" && entry.isCompleted);
  const shippingBadgeLabel = sentenceCaseLabel(
    isDelivered ? "completed" :
      /accept/.test(currentShippingStatus.toLowerCase()) ? "accepted" : currentShippingStatus || "pending",
  );
  const shippingBadgeClass = isDelivered
    ? "completed"
    : /accept/.test(currentShippingStatus.toLowerCase())
      ? "accepted"
      : "in-progress";
  const initialStageIndex = getStageIndex(operation.currentStage);
  const [currentStageIndex, setCurrentStageIndex] = useState(initialStageIndex);
  const [updateDropdownOpen, setUpdateDropdownOpen] = useState(false);
  const [shippingStatusDropdownOpen, setShippingStatusDropdownOpen] = useState(false);
  const [otherStatusModalOpen, setOtherStatusModalOpen] = useState(false);
  const [customLocation, setCustomLocation] = useState("");
  const [responseModal, setResponseModal] = useState("");
  const [responseReason, setResponseReason] = useState("");
  const [offerStatus, setOfferStatus] = useState(operation.offerStatus || "Pending");

  useEffect(() => {
    setCurrentStageIndex(getStageIndex(operation.currentStage));
    setUpdateDropdownOpen(false);
    setShippingStatusDropdownOpen(false);
    setOfferStatus(operation.offerStatus || "Pending");
  }, [operation.id, operation.currentStage, operation.offerStatus]);

  useEffect(() => {
    if (!isDelivered) return;
    setShippingStatusDropdownOpen(false);
    setOtherStatusModalOpen(false);
  }, [isDelivered]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (dropdownRef.current?.contains(event.target)) return;
      if (shippingDropdownRef.current?.contains(event.target)) return;
      setUpdateDropdownOpen(false);
      setShippingStatusDropdownOpen(false);
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  const currentStage = operationStages[currentStageIndex] || operationStages[0];
  const canRespondToOrder =
    String(operation.offerStatus || offerStatus || "").trim().toLowerCase() === "pending";
  const progress = getProgress(currentStageIndex);

  const nextStep = (currentStageIndex === initialStageIndex && operation.nextStep) ||
    stageNextSteps[currentStage.title] || operation.nextStep || "—";

  const handleStageUpdate = (stageIndex) => {
    setCurrentStageIndex(stageIndex);
    setUpdateDropdownOpen(false);
  };

  const saveShippingStatus = async (payload) => {
    if (isDelivered) return false;
    const result = await dispatch(updateOrderStatusAction(id, payload));
    if (!result?.success) return false;

    setOtherStatusModalOpen(false);
    setCustomLocation("");
    dispatch(getOrderAction(id));
    return true;
  };

  const submitOrderResponse = async (event) => {
    event.preventDefault();
    if (!responseModal) return;

    const status = responseModal;
    const payload = { status };
    const reason = responseReason.trim();
    if (status === "rejected" && reason) payload.reason = reason;

    const result = await dispatch(
      respondToOrderAction(operation.databaseId || id, payload),
    );
    if (!result?.success) return;

    setResponseModal("");
    setResponseReason("");
    dispatch(getOrderAction(id));
  };

  const handleShippingStatusSelect = (status) => {
    if (isDelivered) return;
    setShippingStatusDropdownOpen(false);

    if (status === "Other") {
      setCustomLocation("");
      setOtherStatusModalOpen(true);
      return;
    }

    const apiStatus = {
      "Order created": "order_created",
      "Order placed": "order_placed",
      Delivered: "delivered",
    }[status];

    if (apiStatus) saveShippingStatus({ status: apiStatus });
  };

  const handleCustomStatusSubmit = (event) => {
    event.preventDefault();
    const location = customLocation.trim();
    if (!location) return;

    const payload = {
      status: "in_transit",
      customStatus: location,
    };
    saveShippingStatus(payload);
  };

  const handleViewDocument = () => {
    navigate(`/order/${operation.id}/documents`);
  };
  const getOfferStatusClass = (status) => {
    const value = String(status || "").toLowerCase();

    if (value.includes("accept") || value.includes("complete")) {
      return "accepted";
    }

    if (value.includes("reject") || value.includes("cancel")) {
      return "rejected";
    }

    return "pending";
  };

  const sellerEmail = operation.vendorEmail || "—";

  const copyText = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
    } catch (error) {
      console.error("Copy failed:", error);
    }
  };

  if (detailLoading || (!orderDetail && !detailError)) {
    return (
      <div className="dashboard-page operation-modern-page operation-details-reference">
        <button type="button" className="operation-modern-back" onClick={() => navigate("/order")}>
          <span>←</span>{" "}{t("ui.back.to.orders")}</button>
        <LoadingSpinner className="page-loading-state" label={t("ui.loadingOrderDetails")} />
      </div>
    );
  }

  if (detailError || !mappedOperation) {
    return (
      <div className="dashboard-page operation-modern-page operation-details-reference">
        <button type="button" className="operation-modern-back" onClick={() => navigate("/order")}>
          <span>←</span>{" "}{t("ui.back.to.orders")}</button>
        <div className="inquiries-empty-state" role="alert">
          <strong>{text(detailError || "Order details were not found.")}</strong>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-page operation-modern-page operation-details-reference">
      <button
        type="button"
        className="operation-modern-back"
        onClick={() => navigate("/order")}
      >
        <span>←</span>{t("ui.back.to.orders")}</button>
      <section className="reference-card reference-order-header">
        <div className="reference-breadcrumb">{t("ui.order.list")}{" "}<span>/</span> <b>{operation.id}</b>
        </div>
        <div className="d-flex align-items-start justify-content-between">
          <h1>{t("ui.view.order.details")}</h1>
          {canRespondToOrder && (
            <div className="admin-offer-actions">
              <button
                type="button"
                className="admin-offer-reject-button"
                disabled={responseLoading}
                onClick={() => {
                  setResponseReason("");
                  setResponseModal("rejected");
                }}
              >{t("ui.reject")}</button>
              <button
                type="button"
                className="admin-offer-accept-button"
                disabled={responseLoading}
                onClick={() => {
                  setResponseReason("");
                  setResponseModal("accepted");
                }}
              >{t("ui.accept")}</button>
            </div>
          )}
        </div>
        <div className="reference-summary-grid">
          <div>
            <span>{t("ui.operation.id")}</span>
            <strong>{operation.id}</strong>
          </div>
          <div>
            <span>{t("ui.operation.details")}</span>
            <strong>{operation.commodity}</strong>
            {/* <small>{operation.sector}</small> */}
          </div>
          <div>
            <span>{t("ui.operation.date")}</span>
            <strong>{operation.createdDate}</strong>
          </div>
          <div>
            <span>{t("ui.status")}</span>
            <em
              className={`reference-status ${operation.operationStatusClass}`}
            >
              {operation.operationStatus}
            </em>
          </div>
        </div>
        <div className="reference-notice">{t("ui.operation.is.currently.at")}{" "}{currentStage.title.toLowerCase()}:{" "}
          {nextStep}.
        </div>
      </section>
      <section className="reference-card">
        <div className="admin-offer-header">
          <div className="admin-offer-header-content">
            <h2>{t("ui.admin.shared.offer")}</h2>
            <p>{t("ui.review.assigned.vendors.and.the.offer.details.shared.for.this.order")}</p>
          </div>
          <div
            className={`admin-offer-status ${getOfferStatusClass(offerStatus)}`}
          >
            <span className="admin-offer-status-icon">
              {getOfferStatusClass(offerStatus) === "accepted" ? (
                <svg viewBox="0 0 24 24" fill="none">
                  <path
                    d="M6 12.5L10 16.5L18 8.5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ) : getOfferStatusClass(offerStatus) === "rejected" ? (
                <svg viewBox="0 0 24 24" fill="none">
                  <path
                    d="M7 7L17 17M17 7L7 17"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="none">
                  <circle
                    cx="12"
                    cy="12"
                    r="8"
                    stroke="currentColor"
                    strokeWidth="1.8"
                  />

                  <path
                    d="M12 7.5V12L15 14"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </span>

            {translateStatus(offerStatus)}
          </div>
        </div>

        {/* ============================= */}
        {/* MAIN GRID */}
        {/* ============================= */}

        <div className="admin-offer-layout">
          {/* ============================= */}
          {/* LEFT */}
          {/* ============================= */}

          <div className="admin-offer-left">
            {/* VENDOR */}

            <div className="admin-offer-vendor">
              <div className="admin-offer-avatar">
                {operation.vendorName.charAt(0)}
              </div>

              <div className="admin-offer-vendor-content">
                <strong>{operation.vendorName}</strong>

                <p>{operation.vendorOfferTitle}</p>

                <span className="admin-offer-assigned">
                  <svg viewBox="0 0 24 24" fill="none">
                    <circle
                      cx="12"
                      cy="8"
                      r="3"
                      stroke="currentColor"
                      strokeWidth="1.7"
                    />

                    <path
                      d="M6 19C6.5 15.8 8.6 14 12 14C15.4 14 17.5 15.8 18 19"
                      stroke="currentColor"
                      strokeWidth="1.7"
                      strokeLinecap="round"
                    />
                  </svg>{t("ui.assigned")}{" "}{operation.assignedVendorAt}
                </span>
              </div>
            </div>

            {/* ============================= */}
            {/* OFFER DETAILS */}
            {/* ============================= */}

            <div className="admin-offer-details-card">
              <div className="admin-offer-details-heading">
                <h3>{operation.commodity}</h3>

                <p>{t("ui.summary.shared.by.the.assigned.vendor")}</p>
              </div>

              {/* TITLE */}

              <div className="admin-offer-detail-row">
                <div className="admin-offer-detail-icon blue">
                  <svg viewBox="0 0 24 24" fill="none">
                    <path
                      d="M4 8L12 4L20 8L12 12L4 8Z"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinejoin="round"
                    />

                    <path
                      d="M4 8V16L12 20L20 16V8"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinejoin="round"
                    />

                    <path
                      d="M12 12V20"
                      stroke="currentColor"
                      strokeWidth="1.8"
                    />
                  </svg>
                </div>

                <div className="admin-offer-detail-content">
                  <strong>{t("ui.title")}</strong>

                  <span>{operation.vendorOfferTitle}</span>
                </div>
              </div>

              {/* DESCRIPTION */}

              <div className="admin-offer-detail-row">
                <div className="admin-offer-detail-icon purple">
                  <svg viewBox="0 0 24 24" fill="none">
                    <path
                      d="M6 3H14L18 7V21H6V3Z"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinejoin="round"
                    />

                    <path
                      d="M14 3V8H18"
                      stroke="currentColor"
                      strokeWidth="1.8"
                    />

                    <path
                      d="M9 12H15M9 16H14"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>

                <div className="admin-offer-detail-content">
                  <strong>{t("ui.product.description")}</strong>

                  <p>{operation.vendorOfferDescription}</p>
                </div>
              </div>
            </div>
          </div>
          <div className="admin-offer-right">
            {/* PRICE */}

            <div className="admin-offer-price-card">
              <div>
                <span>{t("ui.shared.price")}</span>
                <strong>{operation.vendorOfferPrice}</strong>
              </div>

              <div className="admin-offer-price-icon">
                <svg viewBox="0 0 24 24" fill="none">
                  <ellipse
                    cx="12"
                    cy="7"
                    rx="6"
                    ry="3"
                    stroke="currentColor"
                    strokeWidth="1.7"
                  />

                  <path
                    d="M6 7V11C6 12.7 8.7 14 12 14C15.3 14 18 12.7 18 11V7"
                    stroke="currentColor"
                    strokeWidth="1.7"
                  />

                  <path
                    d="M6 11V15C6 16.7 8.7 18 12 18C15.3 18 18 16.7 18 15V11"
                    stroke="currentColor"
                    strokeWidth="1.7"
                  />
                </svg>
              </div>
            </div>

            {/* CONTACT INFORMATION */}

            <div className="admin-offer-contact-card">
              {/* EMAIL */}

              <div className="admin-offer-contact-row">
                <div className="admin-offer-contact-icon">
                  <svg viewBox="0 0 24 24" fill="none">
                    <rect
                      x="4"
                      y="6"
                      width="16"
                      height="12"
                      rx="2"
                      stroke="currentColor"
                      strokeWidth="1.8"
                    />

                    <path
                      d="M5 8L12 13L19 8"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>

                <div className="admin-offer-contact-content">
                  <span>{t("ui.email")}</span>

                  <strong>{sellerEmail}</strong>
                </div>

                <button
                  type="button"
                  className="admin-offer-copy-btn"
                  onClick={() => copyText(sellerEmail)}
                  title={t("ui.copy.email")}
                >
                  <svg viewBox="0 0 24 24" fill="none">
                    <rect
                      x="8"
                      y="8"
                      width="11"
                      height="11"
                      rx="2"
                      stroke="currentColor"
                      strokeWidth="1.7"
                    />

                    <path
                      d="M16 8V6C16 4.9 15.1 4 14 4H6C4.9 4 4 4.9 4 6V14C4 15.1 4.9 16 6 16H8"
                      stroke="currentColor"
                      strokeWidth="1.7"
                    />
                  </svg>
                </button>
              </div>

              {/* COMPANY */}

              <div className="admin-offer-contact-row">
                <div className="admin-offer-contact-icon">
                  <svg viewBox="0 0 24 24" fill="none">
                    <path
                      d="M7 20V5H15V20"
                      stroke="currentColor"
                      strokeWidth="1.8"
                    />

                    <path
                      d="M4 20H20"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                    />

                    <path
                      d="M10 8H12M10 11H12M10 14H12M16 10H18V20"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>

                <div className="admin-offer-contact-content">
                  <span>{t("ui.name")}</span>

                  <strong>{operation.vendorName}</strong>
                </div>

                <button
                  type="button"
                  className="admin-offer-copy-btn"
                  onClick={() => copyText(operation.vendorName)}
                  title={t("ui.copy.company")}
                >
                  <svg viewBox="0 0 24 24" fill="none">
                    <rect
                      x="8"
                      y="8"
                      width="11"
                      height="11"
                      rx="2"
                      stroke="currentColor"
                      strokeWidth="1.7"
                    />

                    <path
                      d="M16 8V6C16 4.9 15.1 4 14 4H6C4.9 4 4 4.9 4 6V14C4 15.1 4.9 16 6 16H8"
                      stroke="currentColor"
                      strokeWidth="1.7"
                    />
                  </svg>
                </button>
              </div>

              {/* QUOTE */}

              <div className="admin-offer-quote">
                <div className="admin-offer-quote-mark">“</div>

                <p>
                  {operation.vendorResponseReason === "—"
                    ? `Vendor response: ${operation.vendorResponse}`
                    : operation.vendorResponseReason}
                </p>

                <svg
                  className="admin-offer-leaf"
                  viewBox="0 0 32 32"
                  fill="none"
                >
                  <path
                    d="M16 27C16 18 20 11 27 6C27 15 23 22 16 27Z"
                    fill="currentColor"
                  />

                  <path
                    d="M15 24C14 16 10 11 5 9C5 16 8 21 15 24Z"
                    fill="currentColor"
                    opacity="0.7"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="reference-card reference-shipping">
        <div className="reference-section-heading">
          <div>
            <h2>{t("ui.shipping.details")}</h2>
            <p>{t("ui.track.the.current.order.progress.from.placement.to.final.delivery")}</p>
          </div>
          <div className="shipping-header-actions">
            <span className={`shipping-status-pill ${shippingBadgeClass}`}>
              {shippingBadgeLabel}
            </span>
            <div className="operation-update-dropdown" ref={shippingDropdownRef}>
            <button
              type="button"
              className="reference-update-button"
              aria-expanded={shippingStatusDropdownOpen}
              disabled={statusUpdateLoading || isDelivered}
              onClick={() => setShippingStatusDropdownOpen((prev) => !prev)}
            >
              <span aria-hidden="true">✓</span>
              <span>{t("ui.update.status")}</span>
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M3.5 4.5 6 7.5 8.5 4.5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            {shippingStatusDropdownOpen && (
              <div className="operation-update-menu">
                {shippingStatuses.map((status, index) => (
                  <button
                    key={status}
                    type="button"
                    className={`operation-update-option ${getCurrentShippingStatus(operation) === status ? "active" : ""}`}
                    disabled={statusUpdateLoading || isDelivered}
                    onClick={() => handleShippingStatusSelect(status)}
                  >
                    <span>{getCurrentShippingStatus(operation) === status ? "✓" : index + 1}</span>
                    <strong>{translateStatus(status)}</strong>
                  </button>
                ))}
              </div>
            )}
            </div>
          </div>
        </div>
        <div className="reference-timeline">
          {orderHistory.length > 0 ? orderHistory.map((stage) => (
            <div
              className={`reference-timeline-item ${stage.isCompleted ? "done" : stage.isActive ? "active" : "inactive"}`}
              key={stage.id}
            >
              <span className="reference-timeline-dot" aria-hidden="true">
                {stage.isCompleted ? "✓" : ""}
              </span>
              <div>
                <strong>{stage.title}</strong>
                {stage.description && <p>{stage.description}</p>}
                {stage.notes && stage.notes !== stage.description && (
                  <p>{stage.notes}</p>
                )}
                {stage.timelineDate && (
                  <small className="operation-shipment-location">
                    {stage.timelineDate}
                  </small>
                )}
              </div>
            </div>
          )) : (
            <p className="reference-timeline-empty">{t("ui.no.order.history.is.available")}</p>
          )}
        </div>
      </section>
      <section className="reference-card reference-attributes">
        <h2>{t("ui.product.attributes")}</h2>
        <p className="reference-subtitle mb-3">{t("ui.product.information.associated.with.this.operation.request")}</p>
        <div className="reference-attributes-grid">
          <label>{t("ui.sector")}<input value={operation.sector} readOnly />
          </label>
          <label>{t("ui.product")}<input value={operation.commodity} readOnly />
          </label>
          <label>{t("ui.quantity.hpfikr")}<input value={operation.quantity} readOnly />
          </label>
          <label>{t("ui.operation.date.1aa2b5z")}<input value={operation.createdDate} readOnly />
          </label>
          <label className="reference-wide">{t("ui.detailed.requirements")}<textarea
              value={operation.requirementSummary}
              readOnly
            />
          </label>
        </div>
      </section>
      <section className="operation-modern-header">
        <div className="operation-modern-header-content">
          <span className="operation-modern-label">{t("ui.operation.details")}</span>
          <div className="operation-modern-title">
            <h1>{operation.commodity}</h1>
            <span
              className={`operation-modern-status ${operation.operationStatusClass}`}
            >
              {operation.operationStatus}
            </span>
          </div>
          <p>{t("ui.review.the.operation.client.information.product.details.and.current.processing.stage")}</p>
          <div className="operation-modern-meta">
            <span>{operation.id}</span>
            <i />
            <span>{t("ui.created")}{" "}{operation.createdDate}</span>
            <i />
            <span>{operation.country}</span>
          </div>
        </div>
      </section>
      <section className="operation-modern-summary">
        <div className="operation-modern-summary-card">
          <div className="operation-modern-summary-icon">
            <svg viewBox="0 0 24 24" fill="none">
              <path
                d="M5 5H19V19H5V5Z"
                stroke="currentColor"
                strokeWidth="1.6"
              />
              <path
                d="M8 9H16M8 13H13"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            </svg>
          </div>
          <div>
            <span>{t("ui.operation.id")}</span>
            <strong>{operation.id}</strong>
          </div>
        </div>
        <div className="operation-modern-summary-card">
          <div className="operation-modern-summary-icon">
            <svg viewBox="0 0 24 24" fill="none">
              <path
                d="M5 7L12 3L19 7V17L12 21L5 17V7Z"
                stroke="currentColor"
                strokeWidth="1.6"
              />
              <path
                d="M5 7L12 11L19 7"
                stroke="currentColor"
                strokeWidth="1.6"
              />
            </svg>
          </div>
          <div>
            <span>{t("ui.commodity")}</span>
            <strong>{operation.commodity}</strong>
          </div>
        </div>
        <div className="operation-modern-summary-card">
          <div className="operation-modern-summary-icon">
            <svg viewBox="0 0 24 24" fill="none">
              <circle
                cx="12"
                cy="12"
                r="8"
                stroke="currentColor"
                strokeWidth="1.6"
              />

              <path
                d="M8.5 12L11 14.5L16 9.5"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <div>
            <span>{t("ui.current.stage")}</span>
            <strong>{currentStage.title}</strong>
          </div>
        </div>
        <div className="operation-modern-summary-card">
          <div className="operation-modern-summary-icon">
            <svg viewBox="0 0 24 24" fill="none">
              <path
                d="M6 4V7M18 4V7M4 9H20M5 6H19V20H5V6Z"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            </svg>
          </div>
          <div>
            <span>{t("ui.est.completion")}</span>
            <strong>{operation.completionDate}</strong>
            <small
              className={`operation-modern-completion ${operation.completionClass}`}
            >
              {operation.completionText}
            </small>
          </div>
        </div>
      </section>
      <section className="operation-modern-card">
        <div className="operation-modern-card-heading">
          <div>
            <h2>{t("ui.product.details")}</h2>
            <p>{t("ui.commodity.information.associated.with.this.operation")}</p>
          </div>
        </div>
        <div className="operation-modern-product">
          <div className="operation-modern-product-image">
            <img
              src={operation.image}
              alt={operation.commodity}
              onError={(event) => {
                event.currentTarget.style.display = "none";
              }}
            />
          </div>
          <div className="operation-modern-product-content">
            <span>{t("ui.commodity")}</span>
            <h3>{operation.commodity}</h3>
            <p>{operation.grade}</p>
          </div>
        </div>
        <div className="operation-modern-field-grid">
          <div className="operation-modern-field">
            <span>{t("ui.quantity")}</span>
            <strong>{operation.quantity}</strong>
          </div>
          <div className="operation-modern-field">
            <span>{t("ui.target.price")}</span>
            <strong>{operation.targetPrice}</strong>
          </div>
          <div className="operation-modern-field">
            <span>{t("ui.product.quantity")}</span>
            <strong>{operation.productQuantity}</strong>
          </div>
          <div className="operation-modern-field">
            <span>{t("ui.created.date")}</span>
            <strong>{operation.createdDate}</strong>
          </div>
        </div>
      </section>
      <section className="operation-modern-card">
        <div className="operation-modern-card-heading">
          <div>
            <h2>{t("ui.client.information")}</h2>
            <p>{t("ui.client.associated.with.this.operation")}</p>
          </div>
        </div>
        <div className="operation-modern-seller">
          <div className="operation-modern-seller-profile">
            <div className="operation-modern-seller-avatar">
              {operation.seller
                .split(" ")
                .map((word) => word.charAt(0))
                .slice(0, 2)
                .join("")}
            </div>

            <div>
              <h3>{operation.seller}</h3>

              <p>{operation.country}</p>

              <span>{operation.sellerEmail}</span>
            </div>
          </div>

          <div className="operation-modern-seller-info">
            <span>{t("ui.email.phone")}</span>
            <strong>
              {operation.sellerEmail}
              <br />
              {operation.sellerPhone}
            </strong>
          </div>

          <div className="operation-modern-seller-info">
            <span>{t("ui.address")}</span>
            <strong>{operation.address}</strong>
          </div>
        </div>
      </section>
      <section className="operation-modern-card operation-progress-card">
        <div className="operation-modern-card-heading operation-progress-heading">
          <div>
            <h2>{t("ui.operation.progress")}</h2>
            <p>{t("ui.track.the.complete.operation.lifecycle.and.update.its.current.stage")}</p>
          </div>
          <div className="operation-update-dropdown" ref={dropdownRef}>
            <button
              type="button"
              className="operation-update-trigger"
              onClick={() => setUpdateDropdownOpen((prev) => !prev)}
            >{t("ui.update.operation")}<svg
                viewBox="0 0 24 24"
                fill="none"
                className={updateDropdownOpen ? "active" : ""}
              >
                <path
                  d="M7 10L12 15L17 10"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            {updateDropdownOpen && (
              <div className="operation-update-menu">
                <div className="operation-update-menu-title">{t("ui.update.operation.stage")}</div>

                {operationStages.map((stage, index) => {
                  const active = index === currentStageIndex;

                  const completed = index < currentStageIndex;

                  return (
                    <button
                      key={stage.title}
                      type="button"
                      className={`operation-update-option ${
                        active ? "active" : ""
                      }`}
                      onClick={() => handleStageUpdate(index)}
                    >
                      <span className="operation-update-option-icon">
                        {active || completed ? "✓" : index + 1}
                      </span>

                      <span className="operation-update-option-content">
                        <strong>{stage.title}</strong>

                        <small>
                          {active
                            ? t("ui.currentStage")
                            : completed
                              ? t("ui.completed")
                              : t("ui.markAsCurrent")}
                        </small>
                      </span>

                      {active && (
                        <span className="operation-update-current">{t("ui.current.193jzg9")}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        <div className="operation-vertical-progress-section">
          <div className="operation-vertical-progress">
            {operationStages.map((stage, index) => {
              const completed = index <= currentStageIndex;

              const current = index === currentStageIndex;

              const isLast = index === operationStages.length - 1;

              return (
                <div
                  key={stage.title}
                  className={`operation-vertical-step ${
                    completed ? "completed" : ""
                  } ${current ? "current" : ""}`}
                >
                  {/* LEFT */}

                  <div className="operation-vertical-step-left">
                    <div className="operation-vertical-step-circle">
                      {completed && !current ? (
                        <svg viewBox="0 0 24 24" fill="none">
                          <path
                            d="M7 12.5L10.5 16L17 9"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      ) : current ? (
                        <span />
                      ) : (
                        index + 1
                      )}
                    </div>

                    {!isLast && (
                      <div className="operation-vertical-step-line" />
                    )}
                  </div>

                  {/* CONTENT */}

                  <div className="operation-vertical-step-content">
                    <div className="operation-vertical-step-top">
                      <div>
                        <h4>{sentenceCaseLabel(stage.title)}</h4>

                        {current && (
                          <span className="operation-vertical-current-badge">{t("ui.current.193jzg9")}</span>
                        )}
                      </div>

                      <span className="operation-stage-number">{t("ui.stage")}{" "}{index + 1}
                      </span>
                    </div>

                    <p>{stage.description}</p>

                    {current && (
                      <div className="operation-vertical-current-note">
                        <svg viewBox="0 0 24 24" fill="none">
                          <circle
                            cx="12"
                            cy="12"
                            r="8"
                            stroke="currentColor"
                            strokeWidth="1.7"
                          />

                          <path
                            d="M12 8V12.5"
                            stroke="currentColor"
                            strokeWidth="1.7"
                            strokeLinecap="round"
                          />

                          <circle cx="12" cy="16" r="0.8" fill="currentColor" />
                        </svg>{t("ui.operation.is.currently.at.this.stage")}</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      {/* <section className="operation-modern-card">
        <div className="operation-modern-card-heading">
          <div>
            <h2>Next step</h2>

            <p>Next required action for this operation.</p>
          </div>
        </div>

        <div className="operation-modern-next-step">
          <div className="operation-modern-next-icon">
            <svg viewBox="0 0 24 24" fill="none">
              <path
                d="M5 12H19"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />

              <path
                d="M13 6L19 12L13 18"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          <div className="operation-modern-next-content">
            <div className="operation-modern-next-text">
              <span>Next required action</span>

              <strong>{nextStep}</strong>

              <p>
                Complete this step before progressing to the next operation
                stage.
              </p>
            </div>
            {nextStep === "Document submission for customs clearance" && (
              <button
                type="button"
                className="operation-document-view-btn"
                onClick={handleViewDocument}
              >
                <svg viewBox="0 0 24 24" fill="none">
                  <path
                    d="M6 3H14L18 7V21H6V3Z"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinejoin="round"
                  />

                  <path
                    d="M14 3V8H18"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinejoin="round"
                  />

                  <path
                    d="M9 12H15M9 16H14"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                  />
                </svg>
                View Document
              </button>
            )}
          </div>
        </div>
      </section> */}
      <section className="operation-modern-card">
        <div className="operation-modern-card-heading">
          <div>
            <h2>{t("ui.operation.information")}</h2>

            <p>{t("ui.important.dates.status.and.operation.information")}</p>
          </div>
        </div>

        <div className="operation-modern-field-grid operation-modern-card-fields">
          <div className="operation-modern-field">
            <span>{t("ui.operation.id")}</span>

            <strong>{operation.id}</strong>
          </div>

          <div className="operation-modern-field">
            <span>{t("ui.created.date")}</span>

            <strong>{operation.createdDate}</strong>
          </div>

          <div className="operation-modern-field">
            <span>{t("ui.current.stage")}</span>

            <strong>{currentStage.title}</strong>
          </div>

          <div className="operation-modern-field">
            <span>{t("ui.status")}</span>

            <strong>{operation.operationStatus}</strong>
          </div>

          <div className="operation-modern-field">
            <span>{t("ui.estimated.completion")}</span>

            <strong>{operation.completionDate}</strong>
          </div>

          <div className="operation-modern-field">
            <span>{t("ui.completion")}</span>

            <strong
              className={`operation-modern-completion ${operation.completionClass}`}
            >
              {operation.completionText}
            </strong>
          </div>

          <div className="operation-modern-field">
            <span>{t("ui.assigned.vendor")}</span>

            <strong>{operation.vendorName}</strong>
          </div>

          <div className="operation-modern-field">
            <span>{t("ui.country")}</span>

            <strong>{operation.country}</strong>
          </div>

          <div className="operation-modern-field">
            <span>{t("ui.order.record.id")}</span>
            <strong>{operation.databaseId}</strong>
          </div>

          <div className="operation-modern-field">
            <span>{t("ui.inquiry.id")}</span>
            <strong>{operation.inquiryId}</strong>
          </div>

          <div className="operation-modern-field">
            <span>{t("ui.client.id")}</span>
            <strong>{operation.clientId}</strong>
          </div>

          <div className="operation-modern-field">
            <span>{t("ui.client.user.id")}</span>
            <strong>{operation.clientUserId}</strong>
          </div>

          <div className="operation-modern-field">
            <span>{t("ui.assigned.vendor.id")}</span>
            <strong>{operation.vendorId}</strong>
          </div>

          <div className="operation-modern-field">
            <span>{t("ui.updated.date")}</span>
            <strong>{operation.updatedAt}</strong>
          </div>

          <div className="operation-modern-field">
            <span>{t("ui.created.by")}</span>
            <strong>{operation.createdBy}</strong>
          </div>

          <div className="operation-modern-field">
            <span>{t("ui.vendor.approval")}</span>
            <strong>{operation.vendorApprovalStatus}</strong>
          </div>

          <div className="operation-modern-field">
            <span>{t("ui.vendor.active")}</span>
            <strong>{operation.vendorIsActive}</strong>
          </div>

          <div className="operation-modern-field">
            <span>{t("ui.assigned.at")}</span>
            <strong>{operation.assignedVendorAt}</strong>
          </div>

          <div className="operation-modern-field">
            <span>{t("ui.assigned.by")}</span>
            <strong>{operation.assignedVendorBy}</strong>
          </div>

          <div className="operation-modern-field">
            <span>{t("ui.vendor.response")}</span>
            <strong>{operation.vendorResponse}</strong>
          </div>

          <div className="operation-modern-field">
            <span>{t("ui.response.date")}</span>
            <strong>{operation.vendorResponseAt}</strong>
          </div>
        </div>
      </section>
      <section className="operation-modern-card">
        <div className="operation-modern-card-heading">
          <div>
            <h2>{t("ui.operation.activity")}</h2>

            <p>{t("ui.recent.events.and.updates.for.this.operation")}</p>
          </div>
        </div>

        <div className="operation-modern-activity">
          {orderHistory.map((event, index) => (
            <div className="operation-modern-activity-item" key={event.id}>
              <div className="operation-modern-activity-side">
                <span
                  className={`operation-modern-activity-dot ${event.isCompleted ? "completed" : "current"}`}
                />
                {index < orderHistory.length - 1 && (
                  <span className="operation-modern-activity-line" />
                )}
              </div>

              <div className="operation-modern-activity-content">
                <strong>{event.title}</strong>
                {(event.customStatus || event.notes) && (
                  <p>
                    {[event.customStatus, event.notes].filter(Boolean).join(" · ")}
                  </p>
                )}
                <small>
                  {event.completedAt !== "—" ? event.completedAt : event.status}
                </small>
              </div>
            </div>
          ))}
          <div className="operation-modern-activity-item">
            <div className="operation-modern-activity-side">
              <span className="operation-modern-activity-dot current" />
            </div>
            <div className="operation-modern-activity-content">
              <strong>{currentStage.title}</strong>
              <p>{nextStep}</p>

              <small>{t("ui.current.stage")}</small>
            </div>
          </div>
        </div>
      </section>
      {otherStatusModalOpen && (
        <div
          className="operation-status-modal-backdrop"
          role="presentation"
          onClick={(event) => {
            if (event.target === event.currentTarget && !statusUpdateLoading) {
              setOtherStatusModalOpen(false);
            }
          }}
        >
          <form
            className="operation-status-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="other-status-modal-title"
            onSubmit={handleCustomStatusSubmit}
          >
            <header className="operation-status-modal-header">
              <div>
                <h2 id="other-status-modal-title">{t("ui.other.status.update")}</h2>
                <p>{t("ui.add.a.custom.location.update.for.this.order")}</p>
              </div>
              <button
                type="button"
                className="operation-status-modal-close"
                aria-label={t("ui.close.status.update.dialog")}
                disabled={statusUpdateLoading}
                onClick={() => setOtherStatusModalOpen(false)}
              >
                ×
              </button>
            </header>
            <div className="operation-status-modal-fields">
              <label>
                <span className="operation-status-label">{t("ui.current.location")}{" "}<em aria-hidden="true">*</em>
                </span>
                <input
                  autoFocus
                  required
                  value={customLocation}
                  maxLength={120}
                  placeholder={t("ui.e.g.jebel.ali.port.dubai")}
                  onChange={(event) => setCustomLocation(event.target.value)}
                />
              </label>
            </div>
            <footer className="operation-status-modal-actions">
              <button
                type="button"
                className="operation-status-cancel"
                disabled={statusUpdateLoading}
                onClick={() => setOtherStatusModalOpen(false)}
              >{t("ui.cancel")}</button>
              <button
                type="submit"
                className="operation-status-save"
                disabled={statusUpdateLoading || !customLocation.trim()}
              >
                {statusUpdateLoading ? t("ui.saving") : t("ui.saveUpdate")}
              </button>
            </footer>
          </form>
        </div>
      )}
      {responseModal && createPortal(
        <div
          className="order-confirmation-backdrop"
          role="presentation"
          onClick={(event) => {
            if (event.target === event.currentTarget && !responseLoading) {
              setResponseModal("");
              setResponseReason("");
            }
          }}
        >
          <form
            className={`order-confirmation-popup ${responseModal}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="order-confirmation-title"
            aria-describedby="order-confirmation-description"
            onSubmit={submitOrderResponse}
          >
            <span className={`order-confirmation-icon ${responseModal}`} aria-hidden="true">
              {responseModal === "accepted" ? (
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="m5 12.5 4.5 4.5L19 7" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="m6 6 12 12M18 6 6 18" />
                </svg>
              )}
            </span>
            <header className="order-confirmation-header">
              <div>
                <h2 id="order-confirmation-title">
                  {responseModal === "accepted"
                    ? t("ui.acceptOrderTitle")
                    : t("ui.rejectOrderTitle")}
                </h2>
                <p id="order-confirmation-description">
                  {responseModal === "accepted"
                    ? t("ui.acceptOrderConfirmation", { seller: operation.seller })
                    : t("ui.rejectOrderConfirmation", { seller: operation.seller })}
                </p>
              </div>
              <button
                type="button"
                className="order-confirmation-close"
                aria-label={t("ui.close.confirmation")}
                disabled={responseLoading}
                onClick={() => {
                  setResponseModal("");
                  setResponseReason("");
                }}
              >
                ×
              </button>
            </header>
            {responseModal === "rejected" && (
              <div className="order-confirmation-reason-wrap">
                <label className="order-confirmation-reason">
                  <span>{t("ui.reason.optional")}</span>
                  <textarea
                    autoFocus
                    value={responseReason}
                    maxLength={500}
                    placeholder={t("ui.share.why.this.order.is.being.rejected")}
                    onChange={(event) => setResponseReason(event.target.value)}
                  />
                </label>
              </div>
            )}
            <footer className="order-confirmation-actions">
              <button
                type="button"
                className="order-confirmation-cancel"
                disabled={responseLoading}
                onClick={() => {
                  setResponseModal("");
                  setResponseReason("");
                }}
              >{t("ui.cancel")}</button>
              <button
                type="submit"
                className={`order-confirmation-submit ${responseModal}`}
                disabled={responseLoading}
              >
                {responseLoading
                  ? t("ui.submitting")
                  : responseModal === "accepted"
                    ? t("ui.confirmAccept")
                    : t("ui.submitRejection")}
              </button>
            </footer>
          </form>
        </div>,
        document.body,
      )}
    </div>
  );
}
